import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Fingerprint, ScanFace, Trash2, Loader2, Plus, ShieldCheck, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  webauthnAvailable,
  platformAuthenticatorAvailable,
  enrollPasskey,
  listPasskeys,
  deletePasskey,
  renamePasskey,
} from '../../utils/webauthn';
import {
  isNativeApp,
  useIsNative,
  getBiometryInfo,
  nativeBiometricAuth,
  nativeHaptic,
  getAppLockState,
  setAppLock,
} from '../../utils/nativeBridge';
import {
  hasDeviceTokenLocally,
  registerDeviceToken,
  revokeDeviceToken,
  listEnrolledDevices,
  revokeDeviceTokenById,
} from '../../utils/deviceToken';
import { useAuth } from '../../App';

// Legacy localStorage key from the pre-App-Lock native contract. Kept only
// so we can clear it once on first render inside the new toggle (avoids
// stale opt-in state confusing the native app-shell).
const NATIVE_ENROLL_PREF_KEY = 'akf_native_biometric_enrolled';

const fmt = (iso) => {
  if (!iso) return '';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
};

// Best-effort device name suggestion based on User-Agent, so the enroll
// prompt starts with something meaningful instead of "This device".
const suggestDeviceLabel = () => {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Macintosh/i.test(ua)) return 'Mac';
  if (/Android/i.test(ua)) return 'Android phone';
  if (/Windows/i.test(ua)) return 'Windows PC';
  if (/Linux/i.test(ua)) return 'Linux PC';
  return 'This device';
};

export const BiometricSecurityCard = () => {
  const isNative = useIsNative();
  const [supported, setSupported] = useState(false);
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [passkeys, setPasskeys] = useState([]);
  const [removingId, setRemovingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  const editInputRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listPasskeys();
      setPasskeys(rows);
    } catch (e) {
      console.error('list passkeys failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const s = webauthnAvailable();
    setSupported(s);
    if (!s) { setAvailable(false); return; }
    platformAuthenticatorAvailable().then(setAvailable);
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  const handleEnroll = async () => {
    // Ask for a friendly device label first (users often manage 2-3 devices).
    const suggested = suggestDeviceLabel();
    const label = typeof window !== 'undefined'
      ? window.prompt('Name this device (e.g., "iPhone 15", "Work MacBook"):', suggested)
      : suggested;
    if (label === null) return; // user cancelled at prompt
    const trimmed = (label || '').trim().slice(0, 60) || suggested;

    setEnrolling(true);
    try {
      await enrollPasskey(trimmed);
      toast.success('Face ID enabled - unlock instantly next time.');
      await refresh();
    } catch (e) {
      const msg = String(e?.message || '').toLowerCase();
      if (msg.includes('cancel') || msg.includes('abort') || msg.includes('notallowed')) {
        toast.info('Enrollment cancelled.');
      } else if (msg.includes('already registered')) {
        toast.info('This passkey is already registered.');
      } else {
        toast.error(e?.message || 'Could not enable Face ID.');
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this passkey? You\u2019ll need to sign in with your password next time.')) return;
    setRemovingId(id);
    try {
      await deletePasskey(id);
      toast.success('Passkey removed.');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not remove passkey.');
    } finally {
      setRemovingId(null);
    }
  };

  const startRename = (p) => {
    setEditingId(p.id);
    setEditingValue(p.device_label || '');
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingValue('');
  };

  const saveRename = async () => {
    const label = editingValue.trim();
    if (!label) {
      toast.error('Device name cannot be empty.');
      return;
    }
    const current = passkeys.find((x) => x.id === editingId);
    if (current && current.device_label === label) {
      cancelRename();
      return;
    }
    setSavingRename(true);
    try {
      await renamePasskey(editingId, label);
      toast.success('Device name updated.');
      // Optimistic local update, then refresh in background
      setPasskeys((rows) => rows.map((r) => (r.id === editingId ? { ...r, device_label: label } : r)));
      cancelRename();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not rename passkey.');
    } finally {
      setSavingRename(false);
    }
  };

  // In the native wrapper the whole passkey concept is irrelevant - render
  // the OS biometric toggle instead. Done after all hooks so React's
  // hooks-order rule is preserved.
  if (isNative) return <NativeBiometricCard />;

  return (
    <Card data-testid="biometric-security-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ScanFace className="w-5 h-5 text-[#108981]" />
          Passkeys (Face ID / Fingerprint)
        </CardTitle>
        <CardDescription>Unlock AkountFly with your face or fingerprint - no password needed.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!supported && (
          <div className="p-3 rounded-lg bg-amber-50 text-amber-800 text-sm">
            This browser doesn&apos;t support passkeys yet. Try the installed app on iOS or Android.
          </div>
        )}
        {supported && !available && (
          <div className="p-3 rounded-lg bg-slate-50 text-slate-600 text-sm">
            No biometric sensor detected on this device.
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        ) : passkeys.length === 0 ? (
          <div className="flex flex-col items-center py-6 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
            <div className="w-12 h-12 rounded-full bg-[#108981]/10 flex items-center justify-center mb-2">
              <Fingerprint className="w-6 h-6 text-[#108981]" />
            </div>
            <p className="font-semibold text-slate-800">No passkeys yet</p>
            <p className="text-xs text-slate-500 mt-1 px-6">Enable Face ID or fingerprint on this device for one-tap sign-in.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {passkeys.map((p) => {
              const isEditing = editingId === p.id;
              return (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white">
                <div className="w-10 h-10 rounded-full bg-[#108981]/10 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5 text-[#108981]" />
                </div>
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <input
                      ref={editInputRef}
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveRename();
                        else if (e.key === 'Escape') cancelRename();
                      }}
                      maxLength={60}
                      disabled={savingRename}
                      placeholder="Device name"
                      className="w-full px-2 py-1 text-sm font-semibold rounded-lg border border-[#108981] focus:outline-none focus:ring-2 focus:ring-[#108981]/30 disabled:opacity-60"
                      data-testid={`rename-passkey-input-${p.id}`}
                    />
                  ) : (
                    <p className="font-semibold text-slate-800 truncate" data-testid={`passkey-label-${p.id}`}>
                      {p.device_label || 'This device'}
                    </p>
                  )}
                  <p className="text-xs text-slate-500 mt-0.5">
                    Added {fmt(p.created_at)}{p.last_used_at ? ` · Last used ${fmt(p.last_used_at)}` : ''}
                  </p>
                </div>
                {isEditing ? (
                  <>
                    <button
                      onClick={saveRename}
                      disabled={savingRename}
                      className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50 active:scale-90 transition-all disabled:opacity-50"
                      aria-label="Save name"
                      data-testid={`save-rename-passkey-btn-${p.id}`}
                    >
                      {savingRename ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={cancelRename}
                      disabled={savingRename}
                      className="p-2 rounded-lg text-slate-500 hover:bg-slate-50 active:scale-90 transition-all disabled:opacity-50"
                      aria-label="Cancel rename"
                      data-testid={`cancel-rename-passkey-btn-${p.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => startRename(p)}
                      disabled={removingId === p.id}
                      className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-[#108981] active:scale-90 transition-all disabled:opacity-50"
                      aria-label="Rename passkey"
                      data-testid={`rename-passkey-btn-${p.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleRemove(p.id)}
                      disabled={removingId === p.id}
                      className="p-2 rounded-lg text-red-600 hover:bg-red-50 active:scale-90 transition-all disabled:opacity-50"
                      aria-label="Remove passkey"
                      data-testid={`remove-passkey-btn-${p.id}`}
                    >
                      {removingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                  </>
                )}
              </div>
              );
            })}
          </div>
        )}

        <button
          onClick={handleEnroll}
          disabled={!supported || !available || enrolling}
          data-testid="enroll-passkey-btn"
          className="w-full h-12 rounded-xl bg-gradient-to-r from-[#283F8B] to-[#108981] text-white font-bold flex items-center justify-center gap-2 shadow-[0_10px_25px_rgba(40,63,139,0.25)] active:scale-[0.98] transition-transform disabled:opacity-60"
        >
          {enrolling ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Enabling...</>
          ) : (
            <><Plus className="w-4 h-4" /> Add passkey on this device</>
          )}
        </button>
      </CardContent>
    </Card>
  );
};

/**
 * Native wrapper variant: single OS App Lock toggle.
 *
 * The native app owns lock lifecycle now (app-open, background return,
 * idle) - see BackgroundLockHost's isNative short-circuit. This card is
 * the ONLY user-visible surface for turning that lock on/off. The
 * enable path triggers the OS biometric confirm through the bridge; on
 * a failed confirm we leave the toggle OFF.
 *
 * Native contract:
 *   window.NativeApp.getAppLockState() -> { enabled, available, enrolled }
 *   window.NativeApp.setAppLock(next)  -> { success, enabled, error? }
 */
const NativeBiometricCard = () => {
  const [state, setState] = useState({ enabled: false, available: false, enrolled: false });
  const [bioInfo, setBioInfo] = useState({ type: 'none', platform: null });
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth() || {};
  const email = user?.email || '';
  const [signInOn, setSignInOn] = useState(false);
  const [signInBusy, setSignInBusy] = useState(false);
  const [devices, setDevices] = useState([]);

  const loadDevices = useCallback(async () => {
    try { setDevices(await listEnrolledDevices()); } catch { setDevices([]); }
  }, []);

  useEffect(() => {
    if (!email) return;
    setSignInOn(hasDeviceTokenLocally(email));
    loadDevices();
  }, [email, loadDevices]);

  const handleSignInToggle = async (next) => {
    if (signInBusy || !email) return;
    setSignInBusy(true);
    try {
      if (next) {
        const r = await nativeBiometricAuth(`Turn on ${featureLabel} sign-in`);
        if (!r.success) {
          if (r.error !== 'user_cancel' && r.error !== 'system_cancel') toast.error("Couldn't verify. Please try again.");
          return;
        }
        await registerDeviceToken({ email, biometryType: bioInfo.type });
        setSignInOn(true);
        nativeHaptic('success');
        toast.success(`${featureLabel} sign-in is on for this device.`);
      } else {
        await revokeDeviceToken(email);
        setSignInOn(false);
        toast.info(`${featureLabel} sign-in is off. You'll use your password next time.`);
      }
      await loadDevices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update biometric sign-in.");
    } finally {
      setSignInBusy(false);
    }
  };

  const handleRemoveDevice = async (d) => {
    try {
      await revokeDeviceTokenById(d.id);
      if (d.is_this_device) { setSignInOn(false); try { await revokeDeviceToken(email); } catch { /* noop */ } }
      toast.success(`Removed ${d.device_label || 'device'}.`);
      await loadDevices();
    } catch { toast.error("Couldn't remove that device."); }
  };

  useEffect(() => {
    (async () => {
      // getBiometryInfo is only used for the label/icon polish - we don't
      // trust it for availability; the App Lock bridge is the source of
      // truth for whether the row should render at all.
      try {
        const info = await getBiometryInfo();
        setBioInfo({ type: info.type || 'none', platform: info.platform || null });
      } catch { /* noop */ }
      const s = await getAppLockState();
      setState(s);
      setLoaded(true);
      // One-shot cleanup of the legacy opt-in flag - the native app-shell
      // reads its own App Lock state directly now.
      try { localStorage.removeItem(NATIVE_ENROLL_PREF_KEY); } catch { /* noop */ }
    })();
  }, []);

  const featureLabel = (() => {
    if (bioInfo.type === 'face') return 'Face ID';
    if (bioInfo.type === 'fingerprint') return bioInfo.platform === 'ios' ? 'Touch ID' : 'Fingerprint';
    if (bioInfo.type === 'iris') return 'Iris unlock';
    return 'Biometric unlock';
  })();
  const Icon = bioInfo.type === 'face' ? ScanFace : Fingerprint;

  // Hide the whole row when the native side says no biometric hardware.
  // Never show a disabled toggle on a device that literally can't do it.
  if (loaded && !state.available) return null;

  const handleToggle = async (nextEnabled) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await setAppLock(nextEnabled);
      // Trust the native reply, not our optimistic `nextEnabled`. If the
      // user cancelled the OS biometric confirm during enable, `success`
      // is false and `enabled` will still be false - toggle stays OFF.
      setState((s) => ({ ...s, enabled: !!r.enabled }));
      if (nextEnabled && r.success && r.enabled) {
        nativeHaptic('success');
        toast.success(`App Lock is on. ${featureLabel} required on open.`);
      } else if (nextEnabled && !r.success) {
        // User cancelled the biometric confirm, or biometric failed.
        // Stay OFF silently for cancel; toast only for hard errors.
        if (r.error && r.error !== 'user_cancel' && r.error !== 'cancelled') {
          toast.error(`Couldn't enable App Lock. Please try again.`);
        }
      } else if (!nextEnabled && !r.enabled) {
        toast.info(`App Lock is off. AkountFly will open without ${featureLabel}.`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="biometric-security-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#108981]/10 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-[#108981]" strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <CardTitle className="text-[15px] flex items-center gap-2">App Lock</CardTitle>
            <CardDescription className="text-xs">
              Require {featureLabel} every time you open AkountFly.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loaded && state.available && !state.enrolled && (
          <p className="text-sm text-slate-500" data-testid="native-applock-not-enrolled">
            Set up {featureLabel} in your device settings first, then come back to turn App Lock on.
          </p>
        )}

        <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-200 bg-white">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800">
              Require {featureLabel} on open
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              {state.enabled ? 'On. Locks every time you leave the app.' : 'Off. Open without unlocking.'}
            </p>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={state.enabled}
            disabled={busy || !state.enrolled}
            onClick={() => handleToggle(!state.enabled)}
            data-testid="native-applock-toggle"
            className={`relative shrink-0 w-12 h-7 rounded-full transition-colors duration-200 disabled:opacity-50 ${state.enabled ? 'bg-[#108981]' : 'bg-slate-300'}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform duration-200 flex items-center justify-center ${state.enabled ? 'translate-x-5' : 'translate-x-0'}`}
            >
              {busy && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
            </span>
          </button>
        </div>

        {/* Biometric SIGN-IN (device token) - distinct from App Lock */}
        <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-200 bg-white" data-testid="native-biosignin-row">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800">Sign in with {featureLabel}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {signInOn ? 'On. Skip the password when your session expires.' : 'Off. Sign in with your password.'}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={signInOn}
            disabled={signInBusy || !state.enrolled}
            onClick={() => handleSignInToggle(!signInOn)}
            data-testid="native-biosignin-toggle"
            className={`relative shrink-0 w-12 h-7 rounded-full transition-colors duration-200 disabled:opacity-50 ${signInOn ? 'bg-[#108981]' : 'bg-slate-300'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform duration-200 flex items-center justify-center ${signInOn ? 'translate-x-5' : 'translate-x-0'}`}>
              {signInBusy && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
            </span>
          </button>
        </div>

        {devices.length > 0 && (
          <div className="space-y-1.5" data-testid="native-biosignin-devices">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Devices with {featureLabel} sign-in</p>
            {devices.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-slate-50" data-testid={`biosignin-device-${d.id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-slate-800 truncate">
                    {d.device_label || d.platform || 'Device'}{d.is_this_device ? ' · this device' : ''}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {d.last_used_at ? `Last used ${new Date(d.last_used_at).toLocaleDateString()}` : `Added ${new Date(d.created_at).toLocaleDateString()}`}
                  </p>
                </div>
                <button type="button" onClick={() => handleRemoveDevice(d)} className="p-1.5 text-slate-400 hover:text-red-600" aria-label="Remove device" data-testid={`biosignin-device-remove-${d.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BiometricSecurityCard;
